import type { ReactNode } from 'react';
import { DS2Modal } from './DS2Modal';
import type { DS2ModalAction, DS2ModalActionVariant } from './DS2Modal';

/**
 * ConfirmModal — the app-wide confirmation dialog, rendered through the
 * canonical DS2Modal shell. Sole consumer is ConfirmProvider
 * (context/ConfirmContext.tsx); callers use useConfirm()/useConfirmChoice().
 *
 * Visual order is always [cancel, confirm, tertiary?]: the safe action sits
 * first (left / top when stacked) and the destructive action last, per the
 * approved modal design. An action given a `run` executes its async work
 * inside the modal (pending label, dismissal blocked, inline error + stay
 * open on failure) and only resolves after success.
 */

interface Props {
  open:             boolean;
  title:            string;
  message:          string;
  confirmLabel?:    string;
  cancelLabel?:     string;
  /** 'danger' = red confirm (destructive). Default 'default' (gold confirm). */
  variant?:         'default' | 'danger';
  /** Explicit DS2 variants — override the `variant`-derived defaults. */
  confirmVariant?:  DS2ModalActionVariant;
  cancelVariant?:   DS2ModalActionVariant;
  /** Optional third action; presence turns the dialog into 3 choices. */
  tertiaryLabel?:   string;
  tertiaryVariant?: 'default' | 'danger' | DS2ModalActionVariant;
  /** Async work run inside the modal for confirm / tertiary (see DS2Modal). */
  confirmRun?:      () => Promise<void>;
  tertiaryRun?:     () => Promise<void>;
  confirmPendingLabel?:  string;
  tertiaryPendingLabel?: string;
  /** Error message when a `run` rejects; DS2Modal supplies the default. */
  errorText?:       string;
  /** Escape/backdrop dismiss (acts as cancel). Default true. */
  dismissible?:     boolean;
  /** Optional visual slot above the title. */
  icon?:            ReactNode;
  /** Identity of the logical request (queued consecutive dialogs) — see DS2Modal. */
  requestKey?:      string | number;
  onConfirm:        () => void;
  onCancel:         () => void;
  onTertiary?:      () => void;
}

function resolveVariant(
  v: 'default' | 'danger' | DS2ModalActionVariant | undefined,
  fallback: DS2ModalActionVariant,
): DS2ModalActionVariant {
  if (v === 'danger') return 'red';
  if (v === 'default' || v === undefined) return fallback;
  return v;
}

export function ConfirmModal({
  open, title, message,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  variant = 'default', confirmVariant, cancelVariant,
  tertiaryLabel, tertiaryVariant = 'danger',
  confirmRun, tertiaryRun, confirmPendingLabel, tertiaryPendingLabel,
  errorText, dismissible = true, icon, requestKey,
  onConfirm, onCancel, onTertiary,
}: Props) {
  const danger = variant === 'danger';

  const actions: DS2ModalAction[] = [
    {
      id:      'cancel',
      label:   cancelLabel,
      // Safe action: gold beside a red confirm, quiet neutral otherwise.
      variant: cancelVariant ?? (danger ? 'gold' : 'neutral'),
      autoFocus: true,
    },
    {
      id:           'confirm',
      label:        confirmLabel,
      variant:      confirmVariant ?? (danger ? 'red' : 'gold'),
      run:          confirmRun,
      pendingLabel: confirmPendingLabel,
    },
  ];
  if (tertiaryLabel) {
    actions.push({
      id:           'tertiary',
      label:        tertiaryLabel,
      variant:      resolveVariant(tertiaryVariant, 'gold'),
      run:          tertiaryRun,
      pendingLabel: tertiaryPendingLabel,
    });
  }

  const handleAction = (id: string) => {
    if (id === 'confirm')       onConfirm();
    else if (id === 'tertiary') onTertiary?.();
    else                        onCancel();
  };

  return (
    <DS2Modal
      open={open}
      title={title}
      description={message}
      actions={actions}
      onAction={handleAction}
      onDismiss={dismissible ? onCancel : undefined}
      errorText={errorText}
      icon={icon}
      requestKey={requestKey}
      compact
    />
  );
}
